import { useId, useRef, useState } from "react";

import {
  reviewAdminAccessRequest,
  type AdminAccessRequest,
  type AdminReviewStatus,
} from "@/lib/api/admin-access-requests";
import {
  formatReviewedMeta,
  statusToLabel,
} from "@/lib/api/admin-access-request-labels";
import { parseApiError } from "@/lib/api/api-error";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";

const MIN_REJECTION_REASON_LENGTH = 10;
const MAX_REJECTION_REASON_LENGTH = 1000;

type ReviewActionsProps = {
  request: AdminAccessRequest;
  onReviewed: (request: AdminAccessRequest) => void;
};

type ConfirmMode = AdminReviewStatus | null;

function pendingBanner() {
  return (
    <div className="review-banner pending" data-testid="admin-review-banner">
      <i className="ti ti-clock" aria-hidden="true" />
      <span>Pending review — no action taken yet.</span>
    </div>
  );
}

function decidedBanner(request: AdminAccessRequest) {
  const label = statusToLabel(request.status);
  const meta = formatReviewedMeta(
    request.reviewed_at,
    request.reviewed_by_name,
  );
  const detail = meta ? `${label} — ${meta}` : label;
  return (
    <div
      className={`review-banner ${request.status}`}
      data-testid="admin-review-banner"
    >
      <i
        className={`ti ${request.status === "approved" ? "ti-circle-check" : "ti-circle-x"}`}
        aria-hidden="true"
      />
      <span>{detail}</span>
    </div>
  );
}

function trimmedReasonLength(value: string): number {
  return value.trim().length;
}

export function ReviewActions({ request, onReviewed }: ReviewActionsProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmMode, setConfirmMode] = useState<ConfirmMode>(null);
  const [reason, setReason] = useState("");
  const [reasonDirty, setReasonDirty] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const reasonId = useId();
  const reasonHintId = useId();
  const reasonErrorId = useId();

  const isPending = request.status === "pending";
  const trimLen = trimmedReasonLength(reason);
  const reasonTooShort = trimLen > 0 && trimLen < MIN_REJECTION_REASON_LENGTH;
  const showReasonError = reasonDirty && reasonTooShort;
  const canDeny = trimLen >= MIN_REJECTION_REASON_LENGTH;

  function closeModal() {
    if (submitting) {
      return;
    }
    setConfirmMode(null);
    setReason("");
    setReasonDirty(false);
    setError(null);
  }

  useDialogFocusTrap(confirmMode !== null, modalRef, closeModal);

  async function submitReview(status: AdminReviewStatus) {
    if (submitting || !isPending) {
      return;
    }

    if (status === "rejected" && !canDeny) {
      setReasonDirty(true);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const updated = await reviewAdminAccessRequest(
        request.id,
        status,
        status === "rejected" ? reason.trim() : undefined,
      );
      setConfirmMode(null);
      setReason("");
      setReasonDirty(false);
      onReviewed(updated);
    } catch (err) {
      const parsed = parseApiError(err);
      setError(
        parsed.message?.trim() ||
          "We couldn't update this request. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="review-actions" data-testid="admin-review-actions">
      {isPending ? pendingBanner() : decidedBanner(request)}

      {!isPending &&
      request.status === "rejected" &&
      request.rejection_reason ? (
        <div
          className="review-rejection-reason"
          data-testid="admin-review-rejection-reason"
        >
          <span className="review-rejection-reason__label">Reason</span>
          <p className="review-rejection-reason__text">
            {request.rejection_reason}
          </p>
        </div>
      ) : null}

      {isPending ? (
        <div className="review-buttons">
          <button
            type="button"
            className="review-btn approve"
            data-testid="admin-review-approve"
            disabled={submitting}
            onClick={() => {
              setError(null);
              setConfirmMode("approved");
            }}
          >
            <i className="ti ti-circle-check" aria-hidden="true" />
            Approve
          </button>
          <button
            type="button"
            className="review-btn deny"
            data-testid="admin-review-deny"
            disabled={submitting}
            onClick={() => {
              setError(null);
              setReason("");
              setReasonDirty(false);
              setConfirmMode("rejected");
            }}
          >
            <i className="ti ti-circle-x" aria-hidden="true" />
            Deny
          </button>
        </div>
      ) : null}

      {error && confirmMode === null ? (
        <p className="notes-error" data-testid="admin-review-error" role="alert">
          {error}
        </p>
      ) : null}

      {confirmMode ? (
        <div className="review-modal-root" data-testid="admin-review-modal-root">
          <button
            type="button"
            className="review-modal-backdrop"
            aria-label="Close confirmation"
            data-testid="admin-review-modal-backdrop"
            onClick={closeModal}
          />
          <div
            ref={modalRef}
            className="review-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            data-testid="admin-review-modal"
            tabIndex={-1}
          >
            <h3 id={titleId} className="review-modal__title">
              {confirmMode === "approved"
                ? "Approve this request?"
                : "Deny this request?"}
            </h3>
            <p className="review-modal__body">
              {confirmMode === "approved"
                ? "This marks the access request as approved. You can still manage credentials outside the portal."
                : "This marks the access request as denied. The applicant will see the reason you provide."}
            </p>

            {confirmMode === "rejected" ? (
              <div className="review-modal__field">
                <label className="review-modal__label" htmlFor={reasonId}>
                  Reason
                </label>
                <div className="review-modal__textarea-wrap">
                  <textarea
                    id={reasonId}
                    className="review-modal__textarea"
                    data-testid="admin-review-reason"
                    value={reason}
                    maxLength={MAX_REJECTION_REASON_LENGTH}
                    rows={4}
                    aria-invalid={showReasonError}
                    aria-describedby={
                      showReasonError ? reasonErrorId : reasonHintId
                    }
                    onChange={(event) => {
                      setReason(event.target.value);
                      setReasonDirty(true);
                    }}
                    onBlur={() => {
                      setReasonDirty(true);
                    }}
                  />
                  <span
                    className={`review-modal__counter${
                      trimLen >= MAX_REJECTION_REASON_LENGTH
                        ? " review-modal__counter--max"
                        : ""
                    }`}
                    data-testid="admin-review-reason-counter"
                  >
                    {reason.length} / {MAX_REJECTION_REASON_LENGTH}
                  </span>
                </div>
                {showReasonError ? (
                  <p
                    id={reasonErrorId}
                    className="review-modal__error"
                    data-testid="admin-review-reason-error"
                    role="alert"
                  >
                    Enter at least {MIN_REJECTION_REASON_LENGTH} characters
                  </p>
                ) : (
                  <p
                    id={reasonHintId}
                    className="review-modal__hint"
                    data-testid="admin-review-reason-hint"
                  >
                    Minimum {MIN_REJECTION_REASON_LENGTH} characters
                  </p>
                )}
              </div>
            ) : null}

            {error ? (
              <p
                className="review-modal__error"
                data-testid="admin-review-error"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <div className="review-modal__actions">
              <button
                type="button"
                className="review-modal__btn review-modal__btn--secondary"
                data-testid="admin-review-modal-cancel"
                disabled={submitting}
                onClick={closeModal}
              >
                Cancel
              </button>
              {confirmMode === "approved" ? (
                <button
                  type="button"
                  className="review-modal__btn review-modal__btn--approve"
                  data-testid="admin-review-modal-confirm"
                  disabled={submitting}
                  onClick={() => {
                    void submitReview("approved");
                  }}
                >
                  {submitting ? "Approving…" : "Approve request"}
                </button>
              ) : (
                <button
                  type="button"
                  className="review-modal__btn review-modal__btn--deny"
                  data-testid="admin-review-modal-confirm"
                  disabled={submitting || !canDeny}
                  onClick={() => {
                    void submitReview("rejected");
                  }}
                >
                  {submitting ? "Denying…" : "Deny request"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
